import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/RestaurantMenu';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/authStore';
import { problemCode, problemMessage } from '@/api/client';

export default function LoginPage() {
  const { t } = useTranslation();
  const login = useAuthStore((state) => state.login);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
    } catch (caught) {
      // a wrong password is expected input, not a system fault, so it gets its own wording
      setError(
        problemCode(caught) === 'auth.invalid_credentials'
          ? t('auth.invalidCredentials')
          : problemMessage(caught, t('errors.generic')),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      className="flex h-full items-center justify-center p-4"
      sx={{ bgcolor: 'background.default' }}
    >
      <Card className="w-full max-w-sm" elevation={3}>
        <CardContent className="p-6">
          <Box className="mb-6 flex flex-col items-center gap-2">
            <RestaurantIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography variant="h2">{t('app.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('app.subtitle')}
            </Typography>
          </Box>

          <form onSubmit={submit} className="flex flex-col gap-3">
            <TextField
              label={t('auth.username')}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoFocus
              autoComplete="username"
              required
            />
            <TextField
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy || !username || !password}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('auth.signIn')}
            </Button>
          </form>

          <Typography
            variant="caption"
            color="text.secondary"
            className="mt-4 block text-center"
          >
            {t('auth.demoHint')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
